import React from "react";

/**
 * LoadingIndicator
 *
 * Props:
 * - show (boolean)         : control visibility
 * - fullscreen (boolean)   : se true exibe overlay cobrindo toda a tela
 * - centered (boolean)     : centraliza o spinner no container (quando não fullscreen)
 * - size ("sm"|"md"|"lg")  : tamanho visual do spinner (sm, md, lg)
 * - variant ("border"|"grow"): tipo de spinner do Bootstrap
 * - message (string|null)  : texto principal exibido abaixo do spinner
 * - subMessage (string)    : texto secundário menor
 * - backdrop (boolean)     : adiciona um backdrop sem bloquear clicks (quando fullscreen)
 * - className (string)     : classes adicionais para o wrapper
 *
 * Uso:
 * <LoadingIndicator show={true} fullscreen message="Carregando..." />
 * <LoadingIndicator show={isSaving} size="sm" centered message="Salvando..." />
 * 
 * Exemplos de uso
 * 
    Overlay full-screen (padrão para carregamentos globais):
    <LoadingIndicator show={isLoading} fullscreen message="Carregando, aguarde..." />

    Inline, centralizado (ex.: dentro de um card):
    <LoadingIndicator show={isFetching} size="md" centered message="Buscando dados..." />

    Pequeno (para botões ou próximas a textos):
    <LoadingIndicator show={isSaving} size="sm" variant="grow" message="Salvando..." />

    Sem mensagem (apenas spinner):
    <LoadingIndicator show={true} size="lg" message={null} />

 */

export default function LoadingIndicator({
  show = true,
  fullscreen = false,
  centered = true,
  size = "md",
  variant = "border",
  message = "Carregando...",
  subMessage = "",
  backdrop = true,
  className = "",
}) {
  if (!show) return null;

  // map size para classes / dimensões
  const sizeClass = size === "sm" ? "spinner-sm" : size === "lg" ? "spinner-lg" : "spinner-md";
  const spinnerClass = variant === "grow" ? "spinner-grow" : "spinner-border";

  // estilos inline para overlay fullscreen
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 1080, // acima do modal padrão do bootstrap (1050)
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  };

  const backdropStyle = {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 1079,
  };

  // container quando não for fullscreen
  const inlineWrapperStyle = {
    display: "flex",
    alignItems: centered ? "center" : "flex-start",
    justifyContent: centered ? "center" : "flex-start",
    gap: "0.75rem",
  };

  // acessibilidade: informe atualizações de carregamento
  return fullscreen ? (
    <div style={overlayStyle} className={`loading-indicator-fullscreen ${className}`} aria-live="polite" aria-busy="true">
      {backdrop && <div style={backdropStyle} aria-hidden="true" />}
      <div
        role="status"
        className="text-center p-3 rounded"
        style={{
          zIndex: 1081,
          maxWidth: 520,
          width: "min(92%, 520px)",
          background: "rgba(255,255,255,0.92)",
          boxShadow: "0 6px 30px rgba(0,0,0,0.12)",
        }}
      >
        <div className="d-flex flex-column align-items-center">
          <div className={`${spinnerClass} ${sizeClass}`} role="status" aria-hidden="false" />
          {message && <div className="mt-3 fw-semibold">{message}</div>}
          {subMessage && <div className="mt-1 text-muted small">{subMessage}</div>}
        </div>
      </div>
    </div>
  ) : (
    <div className={`loading-indicator-inline ${className}`} style={inlineWrapperStyle} aria-live="polite" aria-busy="true">
      <div className={`${spinnerClass} ${sizeClass}`} role="status" aria-hidden="false" />
      {message && <div className="fw-semibold">{message}</div>}
      {subMessage && <div className="text-muted small">{subMessage}</div>}
    </div>
  );
}

/* CSS helper classes you can place in your global CSS (optional)
.spinner-sm { width: 1rem; height: 1rem; border-width: .12rem; }
.spinner-md { width: 2rem; height: 2rem; border-width: .18rem; }
.spinner-lg { width: 3rem; height: 3rem; border-width: .22rem; }

Se preferir, posso enviar este CSS num arquivo separado ou incorporar via styled-jsx / CSS module.
*/
